"use client";
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Chip,
  Divider,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Fade,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ShareIcon from "@mui/icons-material/Share";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LabelIcon from "@mui/icons-material/Label";
import { useLanguage } from "./LanguageProvider";

export const Blogs = () => {
  const { currentLang, isInitialized } = useLanguage();
  const isRTL = currentLang === "ar";
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  // Translations
  const translations = {
    en: {
      badge: "Our Blog",
      title: "Latest Articles",
      description: "We bring you the latest articles and tips on administrative and legal services",
      readMore: "Read More",
      blogAuthor: "RUKN ALMUROOJ Documents Clearing Services",
    },
    ar: {
      badge: "مدونتنا",
      title: "أحدث المقالات",
      description: "نقدم لك أحدث المقالات والنصائح حول الخدمات الإدارية والقانونية",
      readMore: "اقرأ المزيد",
      blogAuthor: "ركن المروج لخدمات تصفية المعاملات",
    },
  };

  const t = translations[currentLang];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  // Blogs Data - English version
  const blogsDataEn = [
    {
      id: 1,
      title: "How to Follow Up Rental Dispute Center Requests",
      shortDesc: "A comprehensive guide to submitting and tracking requests electronically at the Dubai Rental Dispute Center",
      longDesc: "Following up on Rental Dispute Center requests is a vital service needed by many tenants and landlords. In this article, we provide a comprehensive guide to the steps for submitting and tracking requests electronically, explaining the required documents and procedures to ensure your request proceeds smoothly and quickly.",
      content: `
        <h3>Step 1: Register in the System</h3>
        <p>You must first register on the Rental Dispute Center's electronic portal using your email and phone number.</p>
        
        <h3>Step 2: Submit the Request</h3>
        <p>After registration, you can submit your request by filling out the electronic form and attaching required documents such as the rental contract, notifications, and any other supporting documents.</p>
        
        <h3>Step 3: Track the Request</h3>
        <p>You can track your request status through your dashboard, where you will receive notifications of any updates or new actions related to your request.</p>
        
        <h3>Important Tips</h3>
        <ul>
          <li>Ensure all entered data is correct before submitting the request</li>
          <li>Keep a copy of all attached documents</li>
          <li>Track your request periodically to ensure there are no missing documents</li>
        </ul>
      `,
      image: "https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&w=600",
      date: "March 15, 2026",
      author: "RUKN ALMUROOJ Team",
      category: "Administrative Services",
      readTime: "5 min read",
      views: 1245,
    },
    {
      id: 2,
      title: "Guide to Government Transactions in UAE",
      shortDesc: "Everything you need to know about submitting government transactions electronically in the UAE",
      longDesc: "The UAE has witnessed a major digital transformation in providing government services. In this article, we review the most important electronic platforms for submitting government transactions, and the necessary steps to submit your requests easily and conveniently.",
      content: `
        <h3>Key Government Platforms</h3>
        <ul>
          <li>Hukoomi Platform - for federal services</li>
          <li>Dubai Now Portal - for Dubai Government services</li>
          <li>Abu Dhabi Platform - for Abu Dhabi Government services</li>
        </ul>
        
        <h3>Transaction Submission Steps</h3>
        <p>The steps for submitting transactions vary depending on the type of service and the providing authority, but generally include: creating an account, selecting the service, filling in data, attaching documents, paying fees, and tracking the request.</p>
        
        <h3>Required Documents</h3>
        <p>Documents vary depending on the type of transaction, but typically include: ID copy, trade license, contracts, and required certificates.</p>
      `,
      image: "https://images.pexels.com/photos/4427818/pexels-photo-4427818.jpeg?auto=compress&cs=tinysrgb&w=600",
      date: "March 10, 2026",
      author: "RUKN ALMUROOJ Team",
      category: "Government Transactions",
      readTime: "7 min read",
      views: 892,
    },
    {
      id: 3,
      title: "The Importance of Document Archiving in Business",
      shortDesc: "How to organize and preserve documents professionally to ensure easy access and protection from loss",
      longDesc: "Document archiving is one of the essential processes in any successful organization. In this article, we review the importance of archiving, the best ways to organize documents whether paper or electronic, with practical tips for implementing an effective archiving system.",
      content: `
        <h3>Benefits of Document Archiving</h3>
        <ul>
          <li>Easy access to documents when needed</li>
          <li>Protection of documents from loss or damage</li>
          <li>Saving time and effort in searching for documents</li>
          <li>Compliance with legal and regulatory requirements</li>
        </ul>
        
        <h3>Modern Archiving Systems</h3>
        <p>There are many electronic archiving systems that help organize and index documents, making the search and access process easy.</p>
        
        <h3>Tips for Document Organization</h3>
        <ul>
          <li>Use a clear and logical classification system</li>
          <li>Index documents periodically</li>
          <li>Keep backup copies of important documents</li>
        </ul>
      `,
      image: "https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&w=600",
      date: "March 5, 2026",
      author: "RUKN ALMUROOJ Team",
      category: "Archiving",
      readTime: "4 min read",
      views: 567,
    },
    {
      id: 4,
      title: "How to Inquire About Case Status Electronically",
      shortDesc: "Ways to track cases and judicial requests through electronic systems in the UAE",
      longDesc: "The UAE judicial system offers many electronic services for tracking cases and judicial requests. In this article, we review the steps for inquiring about case status electronically, and the available electronic portals for this service.",
      content: `
        <h3>Electronic Portals for Case Inquiry</h3>
        <ul>
          <li>Dubai Courts Electronic Portal</li>
          <li>Ministry of Justice Portal</li>
          <li>Abu Dhabi Courts Portal</li>
        </ul>
        
        <h3>Inquiry Steps</h3>
        <p>To inquire about a case status, you will need the case number or request number. Then visit the relevant electronic portal, enter the required information, and the case status and any recent updates will appear.</p>
      `,
      image: "https://images.pexels.com/photos/4427818/pexels-photo-4427818.jpeg?auto=compress&cs=tinysrgb&w=600",
      date: "March 1, 2026",
      author: "RUKN ALMUROOJ Team",
      category: "Judicial Services",
      readTime: "6 min read",
      views: 734,
    },
  ];

  // Blogs Data - Arabic version
  const blogsDataAr = [
    {
      id: 1,
      title: "كيفية متابعة طلبات مركز فض المنازعات الإيجارية",
      shortDesc: "دليل شامل لخطوات رفع ومتابعة الطلبات إلكترونياً في مركز فض المنازعات الإيجارية بدبي",
      longDesc: "تعد متابعة طلبات مركز فض المنازعات الإيجارية من الخدمات الحيوية التي يحتاجها الكثير من المستأجرين والملاك. في هذا المقال، نقدم لك دليلاً شاملاً لخطوات رفع ومتابعة الطلبات إلكترونياً، مع شرح للمستندات المطلوبة والإجراءات اللازمة لضمان سير طلبك بشكل سلس وسريع.",
      content: `
        <h3>الخطوة الأولى: التسجيل في النظام</h3>
        <p>يجب عليك أولاً التسجيل في بوابة مركز فض المنازعات الإيجارية الإلكترونية باستخدام البريد الإلكتروني ورقم الهاتف الخاص بك.</p>
        
        <h3>الخطوة الثانية: رفع الطلب</h3>
        <p>بعد التسجيل، يمكنك رفع طلبك عن طريق تعبئة النموذج الإلكتروني وإرفاق المستندات المطلوبة مثل عقد الإيجار، الإخطارات، وأي مستندات داعمة أخرى.</p>
        
        <h3>الخطوة الثالثة: متابعة الطلب</h3>
        <p>يمكنك متابعة حالة طلبك من خلال لوحة التحكم الخاصة بك، حيث ستتلقى إشعارات بأي تحديثات أو إجراءات جديدة تتعلق بطلبك.</p>
        
        <h3>نصائح مهمة</h3>
        <ul>
          <li>تأكد من صحة جميع البيانات المدخلة قبل رفع الطلب</li>
          <li>احتفظ بنسخة من جميع المستندات المرفقة</li>
          <li>تابع طلبك بشكل دوري للتأكد من عدم وجود أي مستندات ناقصة</li>
        </ul>
      `,
      image: "https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&w=600",
      date: "15 مارس 2026",
      author: "فريق ركن المروج",
      category: "الخدمات الإدارية",
      readTime: "5 دقائق",
      views: 1245,
    },
    {
      id: 2,
      title: "دليل المعاملات الحكومية في الإمارات",
      shortDesc: "كل ما تحتاج معرفته عن تقديم المعاملات الحكومية إلكترونياً في دولة الإمارات العربية المتحدة",
      longDesc: "شهدت دولة الإمارات العربية المتحدة تحولاً رقمياً كبيراً في تقديم الخدمات الحكومية. في هذا المقال، نستعرض أهم المنصات الإلكترونية لتقديم المعاملات الحكومية، والخطوات اللازمة لتقديم طلباتك بكل سهولة ويسر.",
      content: `
        <h3>أهم المنصات الحكومية</h3>
        <ul>
          <li>منصة حكومي - للخدمات الاتحادية</li>
          <li>بوابة دبي الآن - لخدمات حكومة دبي</li>
          <li>منصة أبوظبي - لخدمات حكومة أبوظبي</li>
        </ul>
        
        <h3>خطوات تقديم المعاملة</h3>
        <p>تختلف خطوات تقديم المعاملات حسب نوع الخدمة والجهة المقدمة، ولكن بشكل عام تشمل: إنشاء حساب، اختيار الخدمة، تعبئة البيانات، إرفاق المستندات، دفع الرسوم، ومتابعة الطلب.</p>
        
        <h3>المستندات المطلوبة</h3>
        <p>تختلف المستندات حسب نوع المعاملة، ولكن تشمل عادة: صورة الهوية، الرخصة التجارية، العقود، والشهادات المطلوبة.</p>
      `,
      image: "https://images.pexels.com/photos/4427818/pexels-photo-4427818.jpeg?auto=compress&cs=tinysrgb&w=600",
      date: "10 مارس 2026",
      author: "فريق ركن المروج",
      category: "المعاملات الحكومية",
      readTime: "7 دقائق",
      views: 892,
    },
    {
      id: 3,
      title: "أهمية أرشفة المستندات في الأعمال",
      shortDesc: "كيفية تنظيم وحفظ المستندات بشكل احترافي لضمان سهولة الوصول إليها وحمايتها من الضياع",
      longDesc: "تعد أرشفة المستندات من العمليات الأساسية في أي مؤسسة ناجحة. في هذا المقال، نستعرض أهمية الأرشفة، وأفضل الطرق لتنظيم المستندات سواء ورقياً أو إلكترونياً، مع نصائح عملية لتطبيق نظام أرشفة فعال.",
      content: `
        <h3>فوائد أرشفة المستندات</h3>
        <ul>
          <li>سهولة الوصول إلى المستندات عند الحاجة</li>
          <li>حماية المستندات من الضياع أو التلف</li>
          <li>توفير الوقت والجهد في البحث عن المستندات</li>
          <li>الامتثال للمتطلبات القانونية والتنظيمية</li>
        </ul>
        
        <h3>أنظمة الأرشفة الحديثة</h3>
        <p>هناك العديد من أنظمة الأرشفة الإلكترونية التي تساعد في تنظيم المستندات وفهرستها، مما يسهل عملية البحث والوصول إلى المستندات بكل سهولة.</p>
        
        <h3>نصائح لتنظيم المستندات</h3>
        <ul>
          <li>استخدم نظام تصنيف واضح ومنطقي</li>
          <li>قم بفهرسة المستندات بشكل دوري</li>
          <li>احتفظ بنسخ احتياطية من المستندات المهمة</li>
        </ul>
      `,
      image: "https://images.pexels.com/photos/5669602/pexels-photo-5669602.jpeg?auto=compress&cs=tinysrgb&w=600",
      date: "5 مارس 2026",
      author: "فريق ركن المروج",
      category: "الأرشفة",
      readTime: "4 دقائق",
      views: 567,
    },
    {
      id: 4,
      title: "كيفية الاستعلام عن حالة القضايا إلكترونياً",
      shortDesc: "طرق متابعة القضايا والطلبات القضائية عبر الأنظمة الإلكترونية في الإمارات",
      longDesc: "يوفر النظام القضائي في الإمارات العديد من الخدمات الإلكترونية لمتابعة القضايا والطلبات القضائية. في هذا المقال، نستعرض الخطوات اللازمة للاستعلام عن حالة القضايا إلكترونياً، والبوابات الإلكترونية المتاحة لهذه الخدمة.",
      content: `
        <h3>البوابات الإلكترونية للاستعلام عن القضايا</h3>
        <ul>
          <li>بوابة محاكم دبي الإلكترونية</li>
          <li>بوابة وزارة العدل</li>
          <li>بوابة محاكم أبوظبي</li>
        </ul>
        
        <h3>خطوات الاستعلام</h3>
        <p>للتمكن من الاستعلام عن حالة قضية ما، ستحتاج إلى رقم القضية أو رقم الطلب، ثم قم بزيارة البوابة الإلكترونية المختصة، أدخل البيانات المطلوبة، وستظهر لك حالة القضية وأي تحديثات حديثة.</p>
      `,
      image: "https://images.pexels.com/photos/4427818/pexels-photo-4427818.jpeg?auto=compress&cs=tinysrgb&w=600",
      date: "1 مارس 2026",
      author: "فريق ركن المروج",
      category: "الخدمات القضائية",
      readTime: "6 دقائق",
      views: 734,
    },
  ];

  const blogsData = currentLang === "ar" ? blogsDataAr : blogsDataEn;

  const handleOpenDialog = (blog) => {
    setSelectedBlog(blog);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTimeout(() => setSelectedBlog(null), 300);
  };

  const getCardWidth = () => {
    if (isMobile) return "100%";
    if (isTablet) return "calc(50% - 16px)";
    return "calc(25% - 24px)";
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <Box
      id="blogs"
      sx={{
        py: { xs: 6, md: 10 },
        background: "#ffffff",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {/* Section Title */}
          <motion.div variants={itemVariants}>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  bgcolor: "rgba(212,175,55,0.1)",
                  borderRadius: "50px",
                  px: 2,
                  py: 0.5,
                  mb: 2,
                }}
              >
                <Typography variant="body2" sx={{ color: "#d4af37", fontWeight: 500 }}>
                  {t.badge}
                </Typography>
              </Box>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  fontFamily: "Cairo, Roboto",
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  mb: 2,
                  fontSize: { xs: "1.8rem", sm: "2rem", md: "2.5rem" },
                }}
              >
                {t.title}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontFamily: "Cairo, Roboto",
                  color: "#666",
                  maxWidth: "700px",
                  mx: "auto",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                {t.description}
              </Typography>
              <Divider
                sx={{
                  width: "80px",
                  margin: "0 auto",
                  height: "4px",
                  background: "linear-gradient(90deg, #d4af37, #b8942f)",
                  borderRadius: "2px",
                  mt: 3,
                }}
              />
            </Box>
          </motion.div>

          {/* Blogs Cards */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "stretch",
              flexWrap: "wrap",
              gap: { xs: 2, sm: 3, md: 3 },
              mb: 6,
            }}
          >
            {blogsData.map((blog, index) => {
              return (
                <motion.div
                  key={blog.id}
                  variants={itemVariants}
                  whileHover={{
                    y: -8,
                    transition: { type: "spring", stiffness: 300 },
                  }}
                  style={{
                    width: getCardWidth(),
                    maxWidth: isMobile ? "100%" : "350px",
                    minWidth: isMobile ? "auto" : "280px",
                  }}
                >
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: "20px",
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      "&:hover": {
                        boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                      },
                    }}
                    onClick={() => handleOpenDialog(blog)}
                  >
                    {/* Blog Image */}
                    <Box sx={{ position: "relative" }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={blog.image}
                        alt={blog.title}
                        sx={{
                          transition: "transform 0.5s ease",
                          "&:hover": {
                            transform: "scale(1.05)",
                          },
                        }}
                      />
                      <Chip
                        label={blog.category}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 12,
                          [isRTL ? "left" : "right"]: 12,
                          bgcolor: "#d4af37",
                          color: "white",
                          fontSize: "0.7rem",
                          fontWeight: "bold",
                        }}
                      />
                    </Box>

                    <CardContent sx={{ flex: 1, p: 2.5 }}>
                      {/* Meta Info */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1.5,
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <CalendarTodayIcon sx={{ fontSize: 12, color: "#d4af37" }} />
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            {blog.date}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <AccessTimeIcon sx={{ fontSize: 12, color: "#d4af37" }} />
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            {blog.readTime}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <VisibilityIcon sx={{ fontSize: 12, color: "#d4af37" }} />
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            {blog.views}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Cairo, Roboto",
                          fontWeight: "bold",
                          color: "#1a1a2e",
                          fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                          lineHeight: 1.4,
                          mb: 1.5,
                          minHeight: { xs: 50, sm: 55 },
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {blog.title}
                      </Typography>

                      {/* Short Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Cairo, Roboto",
                          color: "#666",
                          lineHeight: 1.6,
                          fontSize: { xs: "0.75rem", sm: "0.8rem" },
                          mb: 2,
                          textAlign: isRTL ? "right" : "left",
                        }}
                      >
                        {blog.shortDesc}
                      </Typography>

                      {/* Author */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: isRTL ? "flex-end" : "flex-start" }}>
                        <PersonIcon sx={{ fontSize: 14, color: "#d4af37" }} />
                        <Typography variant="caption" sx={{ color: "#999" }}>
                          {blog.author}
                        </Typography>
                      </Box>
                    </CardContent>

                    <CardActions sx={{ p: 2.5, pt: 0, justifyContent: isRTL ? "flex-start" : "flex-end" }}>
                      <Button
                        size="small"
                        sx={{
                          color: "#d4af37",
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                      >
                        {t.readMore} <ArrowForwardIcon sx={{ fontSize: 16, [isRTL ? "mr" : "ml"]: 0.5, transform: isRTL ? "rotate(180deg)" : "none" }} />
                      </Button>
                    </CardActions>
                  </Card>
                </motion.div>
              );
            })}
          </Box>
        </motion.div>
      </Container>

      {/* Blog Details Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={400}
        PaperProps={{
          sx: {
            borderRadius: "20px",
            background: "white",
            boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
            m: { xs: 2, sm: 3 },
            maxHeight: "90vh",
          },
        }}
      >
        {selectedBlog && (
          <>
            <DialogTitle
              sx={{
                m: 0,
                p: { xs: 2, sm: 3 },
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #eee",
                fontFamily: "Cairo, Roboto",
                fontWeight: "bold",
                fontSize: { xs: "1rem", sm: "1.2rem" },
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={selectedBlog.category}
                  size="small"
                  sx={{ bgcolor: "#d4af37", color: "white" }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 14, color: "#d4af37" }} />
                  <Typography variant="caption">{selectedBlog.date}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 14, color: "#d4af37" }} />
                  <Typography variant="caption">{selectedBlog.readTime}</Typography>
                </Box>
              </Box>
              <IconButton onClick={handleCloseDialog} sx={{ color: "#999" }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
              {/* Blog Image - مصغرة */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 3,
                }}
              >
                <Box
                  component="img"
                  src={selectedBlog.image}
                  alt={selectedBlog.title}
                  sx={{
                    width: { xs: "90%", sm: "80%", md: "70%" },
                    maxWidth: "500px",
                    height: "400px",
                    borderRadius: "16px",
                    objectFit: "cover",
                  }}
                />
              </Box>

              {/* Blog Title */}
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "Cairo, Roboto",
                  fontWeight: "bold",
                  color: "#1a1a2e",
                  fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.8rem" },
                  mb: 2,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {selectedBlog.title}
              </Typography>

              {/* Blog Long Description */}
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontFamily: "Cairo, Roboto",
                  color: "#333",
                  lineHeight: 1.8,
                  fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  mb: 3,
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                {selectedBlog.longDesc}
              </Typography>

              {/* Blog Content (HTML) */}
              <Box
                dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                sx={{
                  fontFamily: "Cairo, Roboto",
                  color: "#444",
                  lineHeight: 1.8,
                  textAlign: isRTL ? "right" : "left",
                  "& h3": {
                    color: "#d4af37",
                    fontSize: { xs: "1.1rem", sm: "1.2rem" },
                    mb: 1.5,
                    mt: 2,
                  },
                  "& p": {
                    mb: 1.5,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  },
                  "& ul, & ol": {
                    mb: 2,
                    [isRTL ? "pr" : "pl"]: 2,
                  },
                  "& li": {
                    mb: 0.5,
                    fontSize: { xs: "0.85rem", sm: "0.9rem" },
                  },
                }}
              />

              {/* Author Info */}
              <Box
                sx={{
                  mt: 4,
                  pt: 3,
                  borderTop: "1px solid #eee",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  justifyContent: isRTL ? "flex-end" : "flex-start",
                  flexDirection: isRTL ? "row-reverse" : "row",
                }}
              >
                <Avatar sx={{ bgcolor: "#d4af37" }}>
                  <PersonIcon />
                </Avatar>
                <Box sx={{ textAlign: isRTL ? "right" : "left" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1a1a2e" }}>
                    {selectedBlog.author}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#999" }}>
                    {t.blogAuthor}
                  </Typography>
                </Box>
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Blogs;